import { useState, useEffect, useRef } from 'react';
import { readOrgFile, writeOrgFile, listOrgCollection } from '../../storage/FileSystemAdapter';
import { BG_TABLE, BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_FAMILY_DISPLAY } from '../../theme';
import ConfirmDialog from '../shared/ConfirmDialog';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import TagEditor from '../shared/TagEditor';
import InfoBadge from '../shared/InfoBadge';

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:               'Contexto de la organización',
    save:                'Guardar',
    saving:              'Guardando…',
    saveOk:              '✓ Guardado',
    saveError:           'Error al guardar',
    unsavedLabel:        'Cambios sin guardar',
    unsavedConfirm:      'Hay cambios sin guardar. ¿Salir de todas formas?',
    leaveWithoutSaving:  'Salir sin guardar',
    saveAndLeave:        'Guardar y salir',
    loading:             'Cargando…',
    sInternal:           'Cuestiones internas',
    sInternalHelper:     'Factores internos que pueden afectar al SGSI (cultura, estructura, recursos, capacidades, sistemas, etc.)',
    sExternal:           'Cuestiones externas',
    sExternalHelper:     'Factores externos que pueden afectar al SGSI (legales, regulatorios, tecnológicos, competitivos, mercado, etc.)',
    sClimate:            'Cambio climático (AMD1:2024)',
    climateCheckLabel:   'El cambio climático es relevante para el SGSI de esta organización',
    climateNotesLabel:   'Notas sobre relevancia del cambio climático',
    sParties:            'Partes interesadas',
    addParty:            '+ Añadir parte interesada',
    colName:             'Nombre',
    colType:             'Tipo',
    colRequirements:     'Requisitos',
    colISMS:             'SGSI',
    colClimate:          'Clima',
    colActions:          'Acciones',
    editBtn:             'Editar',
    deleteBtn:           '✕',
    deletePartyConfirm:  '¿Eliminar esta parte interesada?',
    partyFormTitleNew:   'Nueva parte interesada',
    partyFormTitleEdit:  'Editar parte interesada',
    fPartyName:          'Nombre',
    fPartyType:          'Tipo',
    fPartyRequirements:  'Requisitos',
    fPartyISMS:          'Abordado por el SGSI',
    fPartyClimate:       'Requisitos relacionados con el cambio climático (AMD1:2024)',
    partyInternal:       'Interno',
    partyExternal:       'Externo',
    partyFormSave:       'Guardar',
    partyFormCancel:     'Cancelar',
    formCancel:          'Cancelar',
    partyValidation:     'El nombre es obligatorio.',
    tagPlaceholder:      'Añadir y pulsar Enter…',
    sReviewDate:         'Fecha de próxima revisión',
    sReviewHistory:      'Historial de revisiones',
    logReviewBtn:        '+ Registrar revisión',
    rhColDate:           'Fecha',
    rhColReviewedBy:     'Revisado por',
    rhColNotes:          'Notas',
    rhFormTitle:         'Registrar revisión',
    rhFormSave:          'Guardar',
    rhFormCancel:        'Cancelar',
    rhValidation:        'La fecha y el revisor son obligatorios.',
    sComplianceObl:      'Obligaciones de cumplimiento',
    coTotal:             'Total',
    coApplicable:        'Aplicables',
    coWithControl:       'Con control vinculado',
    coManagedIn:         'Las obligaciones de cumplimiento se gestionan en el módulo Registro Legal.',
    coLoading:           'Cargando obligaciones…',
    coEmpty:             'No hay obligaciones de cumplimiento registradas.',
  },
  en: {
    title:               'Organizational context',
    save:                'Save',
    saving:              'Saving…',
    saveOk:              '✓ Saved',
    saveError:           'Save error',
    unsavedLabel:        'Unsaved changes',
    unsavedConfirm:      'You have unsaved changes. Leave anyway?',
    leaveWithoutSaving:  'Leave without saving',
    saveAndLeave:        'Save and leave',
    loading:             'Loading…',
    sInternal:           'Internal issues',
    sInternalHelper:     'Internal factors that may affect the ISMS (culture, structure, resources, capabilities, systems, etc.)',
    sExternal:           'External issues',
    sExternalHelper:     'External factors that may affect the ISMS (legal, regulatory, technological, competitive, market, etc.)',
    sClimate:            'Climate change (AMD1:2024)',
    climateCheckLabel:   "Climate change is relevant to this organization's ISMS",
    climateNotesLabel:   'Climate change relevance notes',
    sParties:            'Interested parties',
    addParty:            '+ Add interested party',
    colName:             'Name',
    colType:             'Type',
    colRequirements:     'Requirements',
    colISMS:             'ISMS',
    colClimate:          'Climate',
    colActions:          'Actions',
    editBtn:             'Edit',
    deleteBtn:           '✕',
    deletePartyConfirm:  'Delete this interested party?',
    partyFormTitleNew:   'New interested party',
    partyFormTitleEdit:  'Edit interested party',
    fPartyName:          'Name',
    fPartyType:          'Type',
    fPartyRequirements:  'Requirements',
    fPartyISMS:          'Addressed by ISMS',
    fPartyClimate:       'Climate change requirements (AMD1:2024)',
    partyInternal:       'Internal',
    partyExternal:       'External',
    partyFormSave:       'Save',
    partyFormCancel:     'Cancel',
    formCancel:          'Cancel',
    partyValidation:     'Name is required.',
    tagPlaceholder:      'Add and press Enter…',
    sReviewDate:         'Next review date',
    sReviewHistory:      'Review history',
    logReviewBtn:        '+ Log review',
    rhColDate:           'Date',
    rhColReviewedBy:     'Reviewed by',
    rhColNotes:          'Notes',
    rhFormTitle:         'Log review',
    rhFormSave:          'Save',
    rhFormCancel:        'Cancel',
    rhValidation:        'Date and reviewer are required.',
    sComplianceObl:      'Compliance obligations',
    coTotal:             'Total',
    coApplicable:        'Applicable',
    coWithControl:       'With linked control',
    coManagedIn:         'Compliance obligations are managed in the Legal Register module.',
    coLoading:           'Loading obligations…',
    coEmpty:             'No compliance obligations recorded.',
  },
};

// ── TOOLTIPS ──────────────────────────────────────────────────────────────────
const TOOLTIPS = {
  es: {
    ttInternal:
      'Factores dentro de la organización que pueden influir en su capacidad para lograr los objetivos del SGSI. Ejemplos: cultura organizativa, estructura interna, roles y responsabilidades, capacidades tecnológicas, sistemas de información.',
    ttExternal:
      'Factores fuera de la organización que pueden influir en su capacidad para lograr los objetivos del SGSI. Ejemplos: entorno legal y regulatorio, mercado, tecnología, factores económicos, amenazas del sector.',
    ttClimate:
      'Requerido por AMD1:2024. Indica si los riesgos relacionados con el cambio climático son relevantes para el contexto de tu SGSI. Aplica principalmente a organizaciones con infraestructura física expuesta a eventos climáticos. Para empresas de software sin activos físicos críticos, normalmente no es relevante.',
    ttParties:
      'Persona u organización que puede afectar, verse afectada, o percibirse afectada por las decisiones o actividades relacionadas con la seguridad de la información. Identifica qué requisitos tienen y si el SGSI los aborda.',
    ttRequirements:
      'Requisitos de seguridad de la información que esta parte interesada tiene sobre tu organización. Ejemplos: acuerdo de tratamiento de datos, notificación de incidentes en 72h, certificación ISO 27001, confidencialidad de datos.',
    ttAddressedByISMS:
      'Indica si los requisitos de esta parte interesada están cubiertos por el alcance y controles del SGSI. Si no están cubiertos, considera si deberían incorporarse.',
    ttPartyClimate:
      'AMD1:2024: indica si esta parte interesada tiene requisitos relacionados con el cambio climático que puedan afectar a la seguridad de la información. Para la mayoría de organizaciones tecnológicas, este campo no aplica.',
  },
  en: {
    ttInternal:
      'Factors within the organization that may influence its ability to achieve ISMS objectives. Examples: organizational culture, internal structure, roles and responsibilities, technological capabilities, information systems.',
    ttExternal:
      'Factors outside the organization that may influence its ability to achieve ISMS objectives. Examples: legal and regulatory environment, market, technology, economic factors, sector-specific threats.',
    ttClimate:
      "Required by AMD1:2024. Indicates whether climate change-related risks are relevant to your ISMS context. Mainly applies to organizations with physical infrastructure exposed to climate events. For software companies without critical physical assets, this is typically not relevant.",
    ttParties:
      'A person or organization that can affect, be affected by, or perceive themselves to be affected by decisions or activities related to information security. Identify their requirements and whether the ISMS addresses them.',
    ttRequirements:
      'Information security requirements that this interested party has on your organization. Examples: data processing agreement, incident notification within 72h, ISO 27001 certification, data confidentiality.',
    ttAddressedByISMS:
      "Indicates whether this interested party's requirements are covered by the ISMS scope and controls. If not covered, consider whether they should be incorporated.",
    ttPartyClimate:
      'AMD1:2024: indicates whether this interested party has climate change-related requirements that may affect information security. For most technology organizations, this field does not apply.',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function defaultData(orgId) {
  return {
    id:                    `ctx-${orgId}`,
    orgId,
    internalIssues:        [],
    externalIssues:        [],
    climateChangeRelevant: null,
    climateChangeNotes:    null,
    interestedParties:     [],
    reviewDate:            '',
    reviewHistory:         [],
    updatedAt:             new Date().toISOString(),
  };
}

function blankPartyForm() {
  return {
    id:                       `ip-${Date.now().toString(36)}`,
    name:                     '',
    type:                     'internal',
    requirements:             [],
    addressedByISMS:          false,
    climateChangeRequirements:'',
  };
}

function blankReviewForm() {
  return {
    id:         `rh-${Date.now().toString(36)}`,
    date:       '',
    reviewedBy: '',
    notes:      '',
  };
}

function isOverdue(dateStr) {
  return dateStr && new Date(dateStr) < new Date();
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const inputSt = (err) => ({
  ...FONT_LABEL,
  width: '100%', boxSizing: 'border-box',
  padding: '0.45rem 0.65rem',
  border: `1px solid ${err ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6,
  background: BG_SURFACE, color: TEXT_DEFAULT, outline: 'none',
});

const labelSt = {
  ...FONT_SMALL,
  color: TEXT_MUTED, letterSpacing: '0.03em',
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

const helperSt = {
  ...FONT_LABEL,
  color: TEXT_DISABLED, marginBottom: '0.6rem', lineHeight: 1.5,
};

// ── SECTION TITLE ─────────────────────────────────────────────────────────────
function SectionTitle({ children, tooltip }) {
  return (
    <div style={{
      ...FONT_BODY_STRONG,
      display: 'flex', alignItems: 'center', gap: '0.4rem',
      color: TEXT_HEADING,
      marginBottom: '1rem', paddingBottom: '0.5rem',
      borderBottom: `1px solid ${BORDER_DEFAULT}`,
    }}>
      <span>{children}</span>
      {tooltip && (
        <InfoBadge text={tooltip} />
      )}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, margin: '1.75rem 0' }} />;
}

// ── CONTEXT VIEW ──────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function ContextView({ org, lang, onBack, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t  = T[lang]        || T.en;
  const tt = TOOLTIPS[lang] || TOOLTIPS.en;

  const [data, setData]                       = useState(null);
  const [dirty, setDirty]                     = useState(false);
  const [saveState, setSaveState]             = useState('idle');
  const [partyFormOpen, setPartyFormOpen]     = useState(false);
  const [editingPartyIdx, setEditingPartyIdx] = useState(null);
  const [partyForm, setPartyForm]             = useState(blankPartyForm());
  const [partyFormErrors, setPartyFormErrors] = useState({});
  const [confirmState, setConfirmState] = useState(null);
  const [reviewFormOpen, setReviewFormOpen]   = useState(false);
  const [reviewForm, setReviewForm]           = useState(blankReviewForm());
  const [reviewFormErrors, setReviewFormErrors] = useState({});
  const [legalItems, setLegalItems]           = useState(null);
  const [people, setPeople]                   = useState([]);

  // ── Load ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    readOrgFile(org.id, 'context.json')
      .then(d => setData({ ...defaultData(org.id), ...d }))
      .catch(() => setData(defaultData(org.id)));
  }, [org.id]);

  useEffect(() => {
    listOrgCollection(org.id, 'legal-register')
      .then(items => setLegalItems(items))
      .catch(() => setLegalItems([]));
    readOrgFile(org.id, 'people.json')
      .then(d => setPeople(Array.isArray(d.people) ? d.people : []))
      .catch(() => setPeople([]));
  }, [org.id]);

  // ── beforeunload ──────────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Field helpers ─────────────────────────────────────────────────────────
  const set = (field, val) => {
    setDirty(true);
    setData(prev => prev ? { ...prev, [field]: val } : prev);
  };

  const setPF = (field, val) => setPartyForm(prev => ({ ...prev, [field]: val }));
  const setRF = (field, val) => setReviewForm(prev => ({ ...prev, [field]: val }));

  // ── Save ──────────────────────────────────────────────────────────────────
  // Returns true on successful write, false on save error. The user-facing
  // Save button calls handleSave (no return value consumed). The App-level
  // guard relies on the boolean via the saveRequestTick / onSaveResult
  // contract (see GRC_UI_PATTERNS.md §5).
  const doSave = async () => {
    if (!data || saveState === 'saving') return false;
    setSaveState('saving');
    try {
      const updated = { ...data, updatedAt: new Date().toISOString() };
      await writeOrgFile(org.id, 'context.json', updated);
      setData(updated);
      setDirty(false);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Party form ────────────────────────────────────────────────────────────
  const openAddParty = () => {
    setPartyForm(blankPartyForm());
    setEditingPartyIdx(null);
    setPartyFormErrors({});
    setPartyFormOpen(true);
  };

  const openEditParty = (idx) => {
    const p = data.interestedParties[idx];
    setPartyForm({
      id:                        p.id,
      name:                      p.name,
      type:                      p.type,
      requirements:              Array.isArray(p.requirements) ? p.requirements : [],
      addressedByISMS:           p.addressedByISMS || false,
      climateChangeRequirements: p.climateChangeRequirements || '',
    });
    setEditingPartyIdx(idx);
    setPartyFormErrors({});
    setPartyFormOpen(true);
  };

  const handlePartySave = () => {
    if (!partyForm.name.trim()) {
      setPartyFormErrors({ name: true });
      return;
    }
    const party = {
      id: editingPartyIdx === null
        ? partyForm.id
        : (partyForm.id || data.interestedParties[editingPartyIdx].id),
      name:                      partyForm.name.trim(),
      type:                      partyForm.type,
      requirements:              partyForm.requirements,
      addressedByISMS:           partyForm.addressedByISMS,
      climateChangeRequirements: partyForm.climateChangeRequirements.trim() || null,
    };

    if (editingPartyIdx === null) {
      set('interestedParties', [...data.interestedParties, party]);
    } else {
      const updated = [...data.interestedParties];
      updated[editingPartyIdx] = party;
      set('interestedParties', updated);
    }

    setPartyFormOpen(false);
    setPartyFormErrors({});
  };

  const handleDeleteParty = (idx) => {
    setConfirmState({
      message:      t.deletePartyConfirm,
      confirmLabel: t.deleteBtn,
      onConfirm: () => {
        setConfirmState(null);
        set('interestedParties', data.interestedParties.filter((_, i) => i !== idx));
      },
    });
  };

  // ── Review history handlers ───────────────────────────────────────────────
  const openAddReview = () => {
    setReviewForm(blankReviewForm());
    setReviewFormErrors({});
    setReviewFormOpen(true);
  };

  const handleReviewSave = () => {
    const errors = {
      date:       !reviewForm.date.trim(),
      reviewedBy: !reviewForm.reviewedBy.trim(),
    };
    if (errors.date || errors.reviewedBy) { setReviewFormErrors(errors); return; }
    const entry = {
      id:         reviewForm.id,
      date:       reviewForm.date,
      reviewedBy: reviewForm.reviewedBy,
      notes:      reviewForm.notes.trim(),
    };
    set('reviewHistory', [...(data.reviewHistory || []), entry]);
    setReviewFormOpen(false);
    setReviewFormErrors({});
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  const saveLabel = saveState === 'saving' ? t.saving
    : saveState === 'ok'    ? t.saveOk
    : t.save;

  const isSaving = saveState === 'saving';

  const legalSummary = legalItems === null ? null : {
    total:       legalItems.length,
    applicable:  legalItems.filter(lr => lr.applicable).length,
    withControl: legalItems.filter(lr => Array.isArray(lr.linkedControlIds) && lr.linkedControlIds.length > 0).length,
  };

  const errorBannerSt = {
    ...FONT_LABEL,
    background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
    borderRadius: 6, padding: '0.35rem 0.75rem',
    marginBottom: '1rem',
  };

  const cardSt = {
    background: BG_SURFACE,
    border: `1px solid ${STATUS_NEUTRAL.bg}`,
    borderRadius: 10,
    padding: '1.5rem 1.75rem',
  };

  // Shared inline-label-with-tooltip row style
  const labelRowSt = {
    display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.3rem',
  };
  const labelSpanSt = {
    ...FONT_SMALL,
    color: TEXT_MUTED, letterSpacing: '0.03em', textTransform: 'uppercase',
  };

  return (
    <div>
      {/* Header bar */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '2rem',
      }}>
        <div>
          <h2 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: 0,
          }}>{t.title}</h2>
        </div>
      </div>

      {saveState === 'error' && (
        <div style={errorBannerSt}>{t.saveError}</div>
      )}

      <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        {/* Section 1 — Internal issues */}
        <div style={cardSt}>
          <SectionTitle tooltip={tt.ttInternal}>{t.sInternal}</SectionTitle>
          <p style={helperSt}>{t.sInternalHelper}</p>
          <TagEditor
            tags={data.internalIssues}
            onChange={val => set('internalIssues', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 2 — External issues */}
        <div style={cardSt}>
          <SectionTitle tooltip={tt.ttExternal}>{t.sExternal}</SectionTitle>
          <p style={helperSt}>{t.sExternalHelper}</p>
          <TagEditor
            tags={data.externalIssues}
            onChange={val => set('externalIssues', val)}
            placeholder={t.tagPlaceholder}
          />
        </div>

        {/* Section 3 — Climate change */}
        <div style={cardSt}>
          <SectionTitle>{t.sClimate}</SectionTitle>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: data.climateChangeRelevant ? '1rem' : 0,
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={data.climateChangeRelevant || false}
                onChange={e => set('climateChangeRelevant', e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer', accentColor: BRAND_PRIMARY }}
              />
              <span style={{
                ...FONT_LABEL, color: TEXT_DEFAULT,
              }}>{t.climateCheckLabel}</span>
            </label>
            <InfoBadge text={tt.ttClimate} />
          </div>
          {data.climateChangeRelevant && (
            <div>
              <label style={labelSt}>{t.climateNotesLabel}</label>
              <textarea
                rows={3}
                value={data.climateChangeNotes || ''}
                onChange={e => set('climateChangeNotes', e.target.value || null)}
                style={{ ...inputSt(false), resize: 'vertical' }}
              />
            </div>
          )}
        </div>

        {/* Section 4 — Interested parties */}
        <div style={cardSt}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: '1rem',
          }}>
            <SectionTitle tooltip={tt.ttParties}>{t.sParties}</SectionTitle>
            <button
              onClick={openAddParty}
              disabled={partyFormOpen}
              style={{
                ...FONT_LABEL,
                background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                borderRadius: 7, padding: '0.35rem 0.9rem',
                cursor: partyFormOpen ? 'not-allowed' : 'pointer',
                opacity: partyFormOpen ? 0.5 : 1,
              }}
            >{t.addParty}</button>
          </div>

          {data.interestedParties.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: partyFormOpen ? '1.25rem' : 0 }}>
              <table style={{
                ...FONT_LABEL,
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: BG_TABLE }}>
                    {[t.colName, t.colType, t.colRequirements, t.colISMS, t.colClimate, t.colActions].map(col => (
                      <th key={col} style={{
                        padding: '0.5rem 0.6rem', textAlign: 'left',
                        fontWeight: 600, color: TEXT_MUTED, fontSize: '0.68rem',
                        letterSpacing: '0.03em', borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                        whiteSpace: 'nowrap',
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.interestedParties.map((p, idx) => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                      <td style={{ padding: '0.5rem 0.6rem', fontWeight: 500, color: TEXT_HEADING }}>{p.name}</td>
                      <td style={{ padding: '0.5rem 0.6rem' }}>
                        <span style={{
                          background: p.type === 'internal' ? '#e0e7ef' : '#f0fdf4',
                          color: p.type === 'internal' ? TEXT_DEFAULT : BRAND_PRIMARY_DARK,
                          borderRadius: 4, padding: '0.15rem 0.45rem',
                          fontSize: '0.7rem', fontWeight: 600,
                        }}>
                          {p.type === 'internal' ? t.partyInternal : t.partyExternal}
                        </span>
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', color: TEXT_MUTED }}>
                        {Array.isArray(p.requirements) ? p.requirements.length : 0}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                        {p.addressedByISMS
                          ? <span style={{ color: BRAND_PRIMARY_DARK, fontWeight: 700 }}>✓</span>
                          : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                        {p.climateChangeRequirements
                          ? <span style={{ color: BRAND_PRIMARY_DARK, fontWeight: 700 }}>✓</span>
                          : <span style={{ color: '#d1d5db' }}>—</span>}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', whiteSpace: 'nowrap' }}>
                        <button
                          onClick={() => openEditParty(idx)}
                          style={{
                            ...FONT_SMALL,
                            background: 'none', border: `1px solid ${BORDER_DEFAULT}`,
                            borderRadius: 5, padding: '0.2rem 0.55rem',
                            cursor: 'pointer', color: TEXT_DEFAULT,
                            marginRight: '0.3rem',
                          }}
                        >{t.editBtn}</button>
                        <button
                          onClick={() => handleDeleteParty(idx)}
                          style={{
                            ...BTN_DESTRUCTIVE_OUTLINE,
                            ...FONT_SMALL,
                            borderRadius: 5, padding: '0.2rem 0.4rem',
                            cursor: 'pointer',
                          }}
                        >{t.deleteBtn}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Inline party form */}
          {partyFormOpen && (
            <div style={{
              background: BG_TABLE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
              borderRadius: 8, padding: '1.25rem',
            }}>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_HEADING, margin: '0 0 1rem',
              }}>
                {editingPartyIdx === null ? t.partyFormTitleNew : t.partyFormTitleEdit}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={labelSt}>{t.fPartyName} <span style={{ color: ERROR_TEXT }}>*</span></label>
                  <input
                    type="text" value={partyForm.name}
                    onChange={e => setPF('name', e.target.value)}
                    style={inputSt(partyFormErrors.name)}
                  />
                  {partyFormErrors.name && (
                    <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0.25rem 0 0' }}>
                      {t.partyValidation}
                    </p>
                  )}
                </div>
                <div>
                  <label style={labelSt}>{t.fPartyType}</label>
                  <select
                    value={partyForm.type}
                    onChange={e => setPF('type', e.target.value)}
                    style={inputSt(false)}
                  >
                    <option value="internal">{t.partyInternal}</option>
                    <option value="external">{t.partyExternal}</option>
                  </select>
                </div>
              </div>

              {/* Requirements with tooltip */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={labelRowSt}>
                  <span style={labelSpanSt}>{t.fPartyRequirements}</span>
                  <InfoBadge text={tt.ttRequirements} />
                </div>
                <TagEditor
                  tags={partyForm.requirements}
                  onChange={val => setPF('requirements', val)}
                  placeholder={t.tagPlaceholder}
                />
              </div>

              {/* Addressed by ISMS with tooltip */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={partyForm.addressedByISMS}
                      onChange={e => setPF('addressedByISMS', e.target.checked)}
                      style={{ width: 15, height: 15, cursor: 'pointer', accentColor: BRAND_PRIMARY }}
                    />
                    <span style={{
                      ...FONT_LABEL, color: TEXT_DEFAULT,
                    }}>{t.fPartyISMS}</span>
                  </label>
                  <InfoBadge text={tt.ttAddressedByISMS} />
                </div>
              </div>

              {/* Climate change requirements with tooltip */}
              <div style={{ marginBottom: '1rem' }}>
                <div style={labelRowSt}>
                  <span style={labelSpanSt}>{t.fPartyClimate}</span>
                  <InfoBadge text={tt.ttPartyClimate} />
                </div>
                <textarea
                  rows={2}
                  value={partyForm.climateChangeRequirements}
                  onChange={e => setPF('climateChangeRequirements', e.target.value)}
                  style={{ ...inputSt(false), resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button
                  onClick={handlePartySave}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON,
                    border: 'none',
                    borderRadius: 7, padding: '0.4rem 1.1rem', cursor: 'pointer',
                  }}
                >{t.partyFormSave}</button>
                <button
                  onClick={() => { setPartyFormOpen(false); setPartyFormErrors({}); }}
                  style={{
                    ...BTN_SECONDARY,
                    ...FONT_LABEL,
                    borderRadius: 7,
                    padding: '0.4rem 0.9rem', cursor: 'pointer',
                  }}
                >{t.partyFormCancel}</button>
              </div>
            </div>
          )}
        </div>

        {/* Section 5 — Review date */}
        <div style={cardSt}>
          <SectionTitle>{t.sReviewDate}</SectionTitle>
          <input
            type="date"
            value={data.reviewDate || ''}
            onChange={e => set('reviewDate', e.target.value)}
            style={{ ...inputSt(isOverdue(data.reviewDate)), maxWidth: 220 }}
          />
        </div>

        {/* Section 6 — Review history */}
        <div style={cardSt}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: '1rem',
          }}>
            <SectionTitle>{t.sReviewHistory}</SectionTitle>
            <button
              onClick={openAddReview}
              disabled={reviewFormOpen}
              style={{
                ...FONT_LABEL,
                background: TEXT_HEADING, color: SURFACE_WHITE, border: 'none',
                borderRadius: 7, padding: '0.35rem 0.9rem',
                cursor: reviewFormOpen ? 'not-allowed' : 'pointer',
                opacity: reviewFormOpen ? 0.5 : 1,
              }}
            >{t.logReviewBtn}</button>
          </div>

          {(data.reviewHistory || []).length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: reviewFormOpen ? '1.25rem' : 0 }}>
              <table style={{
                ...FONT_LABEL,
                width: '100%', borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{ background: BG_TABLE }}>
                    {[t.rhColDate, t.rhColReviewedBy, t.rhColNotes].map(col => (
                      <th key={col} style={{
                        padding: '0.5rem 0.6rem', textAlign: 'left',
                        fontWeight: 600, color: TEXT_MUTED, fontSize: '0.68rem',
                        letterSpacing: '0.03em', borderBottom: `1px solid ${STATUS_NEUTRAL.bg}`,
                        whiteSpace: 'nowrap',
                      }}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.reviewHistory || []).map(entry => (
                    <tr key={entry.id} style={{ borderBottom: `1px solid ${BG_MUTED}` }}>
                      <td style={{ padding: '0.5rem 0.6rem', whiteSpace: 'nowrap' }}>{entry.date}</td>
                      <td style={{ padding: '0.5rem 0.6rem', fontWeight: 500, color: TEXT_HEADING }}>
                        {resolvePerson(entry.reviewedBy, people)}
                      </td>
                      <td style={{ padding: '0.5rem 0.6rem', color: TEXT_MUTED }}>{entry.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Inline review form */}
          {reviewFormOpen && (
            <div style={{
              background: BG_TABLE, border: `1px solid ${STATUS_NEUTRAL.bg}`,
              borderRadius: 8, padding: '1.25rem',
            }}>
              <p style={{
                ...FONT_BODY_STRONG,
                color: TEXT_HEADING, margin: '0 0 1rem',
              }}>{t.rhFormTitle}</p>

              {(reviewFormErrors.date || reviewFormErrors.reviewedBy) && (
                <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.75rem' }}>
                  {t.rhValidation}
                </p>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div>
                  <label style={labelSt}>{t.rhColDate} <span style={{ color: ERROR_TEXT }}>*</span></label>
                  <input
                    type="date"
                    value={reviewForm.date}
                    onChange={e => setRF('date', e.target.value)}
                    style={inputSt(reviewFormErrors.date)}
                  />
                </div>
                <div>
                  <label style={labelSt}>{t.rhColReviewedBy} <span style={{ color: ERROR_TEXT }}>*</span></label>
                  <PeopleSelector
                    orgId={org.id}
                    lang={lang}
                    value={reviewForm.reviewedBy}
                    onChange={val => setRF('reviewedBy', val)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={labelSt}>{t.rhColNotes}</label>
                <textarea
                  rows={2}
                  value={reviewForm.notes}
                  onChange={e => setRF('notes', e.target.value)}
                  style={{ ...inputSt(false), resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.6rem' }}>
                <button
                  onClick={handleReviewSave}
                  style={{
                    ...BTN_PRIMARY,
                    ...FONT_BUTTON,
                    border: 'none',
                    borderRadius: 7, padding: '0.4rem 1.1rem', cursor: 'pointer',
                  }}
                >{t.rhFormSave}</button>
                <button
                  onClick={() => { setReviewFormOpen(false); setReviewFormErrors({}); }}
                  style={{
                    ...BTN_SECONDARY,
                    ...FONT_LABEL,
                    borderRadius: 7,
                    padding: '0.4rem 0.9rem', cursor: 'pointer',
                  }}
                >{t.rhFormCancel}</button>
              </div>
            </div>
          )}
        </div>

        {/* Section 7 — Compliance obligations summary */}
        <div style={cardSt}>
          <SectionTitle>{t.sComplianceObl}</SectionTitle>
          {legalSummary === null ? (
            <p style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>
              {t.coLoading}
            </p>
          ) : legalSummary.total === 0 ? (
            <p style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>
              {t.coEmpty}
            </p>
          ) : (
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.85rem' }}>
              {[
                { label: t.coTotal,       value: legalSummary.total },
                { label: t.coApplicable,  value: legalSummary.applicable },
                { label: t.coWithControl, value: legalSummary.withControl },
              ].map(({ label, value }) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{
                    fontFamily: FONT_FAMILY_DISPLAY, fontWeight: 800,
                    fontSize: '1.6rem', color: TEXT_HEADING, lineHeight: 1,
                  }}>{value}</div>
                  <div style={{
                    ...FONT_TINY,
                    color: TEXT_MUTED, letterSpacing: '0.03em',
                    textTransform: 'uppercase', marginTop: '0.25rem',
                  }}>{label}</div>
                </div>
              ))}
            </div>
          )}
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>
            {t.coManagedIn}
          </p>
        </div>

      </div>
      </fieldset>
      <Divider />
      <ConfirmDialog
        open={confirmState !== null}
        message={confirmState?.message}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel || 'Confirm'}
        cancelLabel={t.formCancel}
        secondaryAction={confirmState?.secondaryAction}
      />

      {/* Sticky action bar */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
        marginTop: '1.5rem',
        padding: '0.85rem 1.5rem', background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {dirty && (
            <span style={{
              ...FONT_LABEL,
              color: UNSAVED_AMBER, letterSpacing: '0.03em',
            }}>{t.unsavedLabel}</span>
          )}
          <button
            onClick={handleSave}
            disabled={!dirty || saveState === 'saving'}
            style={{
              ...BTN_PRIMARY,
              ...FONT_BUTTON,
              border: 'none',
              borderRadius: 8, padding: '0.45rem 1.1rem',
              cursor: (!dirty || saveState === 'saving') ? 'not-allowed' : 'pointer',
              opacity: !dirty ? 0.45 : 1,
            }}
          >{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}
